// Hideo - "Trends" page logic (page id keywords) (Tools section, with Hooks and Scripts).
// 1) Search: YouTube search-bar suggestions (what people really type), fetched from the same public autocomplete
//    service (JSONP, no key, no account), in the UI language. Variants with question words / "best" and, with
//    "More ideas", with the letters A–Z. No search volumes: they are not available for free.
// 2) The project's keyword bank (data.keywords = [{ id, text, status: 'da usare'|'usata', projectId }]; status values
//    are stored, never translated): a keyword becomes an idea in the plan (with the keyword already in the SEO) or a
//    hook; it can go into the Google Trends charts (./trends.ts); link to YouTube search. "Linked" videos are those with that keyword in their SEO card.
import { LANG, ucFirst, type Lang } from '../i18n'
import type { Hook, Keyword, ProfileData, Video, VideoSeo } from '../data/types'
import { newVideo } from '../data/newVideo'
import { removeItem } from '../data/items'

/** Longest keyword kept in the bank */
export const KW_MAX = 80
/** Bank filter chips */
export type KwFilter = 'all' | 'todo' | 'used'

/** Comparison form of a keyword: trimmed, single spaces, lower case */
export const kwNorm = (s: unknown): string => String(s ?? '').trim().replace(/\s+/g, ' ').toLowerCase()
export const kwYouTube = (q: string): string => `https://www.youtube.com/results?search_query=${encodeURIComponent(q)}`
/** Title of an idea or hook made from a keyword: first letter in upper case */
export const kwTitle = (text: string): string => ucFirst(text)

// ===== YouTube suggestions =====
/** Address of the autocomplete service for one phrase, asked and answered in UTF-8 (by default the answer is
 *  ISO-8859-1, which loses letters outside it); the answer calls window[callback] */
export const suggestUrl = (q: string, callback: string, lang: Lang = LANG): string =>
  `https://suggestqueries.google.com/complete/search?client=youtube&ds=yt&ie=utf-8&oe=utf-8&${lang === 'it' ? 'hl=it&gl=IT' : 'hl=en&gl=US'}&q=${encodeURIComponent(q)}&callback=${callback}`

/** Phrases asked for a topic (already normalised): the topic, question-word variants and, with more, "topic a"…"topic z".
 *  The Italian list is the original one, the English one its natural equivalent. */
export function searchQueries(q: string, more = false, lang: Lang = LANG): string[] {
  const variants = lang === 'it' ? [`${q} come`, `come ${q}`, `perché ${q}`, `quanto ${q}`, `migliori ${q}`]
    : [`how to ${q}`, `why ${q}`, `what is ${q}`, `best ${q}`, `${q} vs`]
  return [q, ...variants].concat(more ? 'abcdefghijklmnopqrstuvwxyz'.split('').map(l => `${q} ${l}`) : [])
}

/** Suggestions out of the service's answer ([query, [suggestion | [suggestion, …]], …]) */
export const parseSuggestions = (data: unknown): string[] => {
  const list = Array.isArray(data) && Array.isArray(data[1]) ? data[1] : []
  return list.map(x => (Array.isArray(x) ? String(x[0]) : String(x)))
}

let kwCb = 0
const TIMEOUT = 7000
/** YouTube suggestions for a phrase (null if the service does not answer). JSONP: callbacks `__pedlyKw<n>` (never rename) */
export function ytSuggest(q: string): Promise<string[] | null> {
  return new Promise(res => {
    const name = '__pedlyKw' + (++kwCb), s = document.createElement('script')
    const w = window as unknown as Record<string, unknown>
    let timer: ReturnType<typeof setTimeout> | undefined
    const done = (r: string[] | null) => {
      clearTimeout(timer)
      try { delete w[name] } catch { w[name] = undefined }
      s.remove()
      res(r)
    }
    w[name] = (data: unknown) => done(parseSuggestions(data))
    s.onerror = () => done(null)
    timer = setTimeout(() => done(null), TIMEOUT)
    s.src = suggestUrl(q, name)
    document.head.appendChild(s)
  })
}

/** Result of a search: the suggestions (normalised, without duplicates and without the topic itself) and whether the
 *  service answered at least once */
interface SuggestResult {
  results: string[]
  ok: boolean
}
/** Asks every phrase of searchQueries (6 at a time) and merges the answers in order */
export async function searchSuggestions(q: string, more = false, suggest: (q: string) => Promise<string[] | null> = ytSuggest,
  lang: Lang = LANG): Promise<SuggestResult> {
  const qs = searchQueries(q, more, lang)
  const out: string[] = []
  let ok = false
  for (let i = 0; i < qs.length; i += 6) {
    const part = await Promise.all(qs.slice(i, i + 6).map(suggest))
    part.forEach(r => {
      if (!r) return
      ok = true
      r.forEach(x => { const k = kwNorm(x); if (k && k !== q && !out.includes(k)) out.push(k) })
    })
  }
  return { results: out, ok }
}

// ===== Keyword bank =====
/** The keyword is already in the project's bank (same words, any case or spacing) */
export const kwInBank = (keywords: readonly Keyword[], projectId: string, text: string): boolean =>
  keywords.some(k => k.projectId === projectId && kwNorm(k.text) === kwNorm(text))

/** Adds a keyword at the top of the project's bank (status "da usare"); false if empty or already there */
export function addKeyword(d: ProfileData, text: unknown, projectId: string, now = Date.now()): boolean {
  const kw = String(text ?? '').trim().slice(0, KW_MAX)
  if (!kw || kwInBank(d.keywords, projectId, kw)) return false
  d.keywords.unshift({ id: 'kw_' + now + Math.random().toString(36).slice(2, 5), text: kw, status: 'da usare', projectId })
  return true
}

const seoKw = (v: Video): { kw: string; kw2: string } => {
  const s = v.seo && typeof v.seo === 'object' ? v.seo : {}
  return { kw: typeof s.kw === 'string' ? s.kw : '', kw2: typeof s.kw2 === 'string' ? s.kw2 : '' }
}
/** The normalised keywords of a video's SEO card: the main one and the comma-separated secondary ones */
const videoKwKeys = (v: Video): Set<string> => {
  const s = seoKw(v)
  return new Set([kwNorm(s.kw), ...s.kw2.split(',').map(x => x.trim()).filter(Boolean).map(kwNorm)])
}
/** Videos using a keyword in their SEO card (main keyword, or one of the comma-separated secondary ones) */
export function kwVideos(d: ProfileData, text: string, index?: Map<string, Video[]>): Video[] {
  const n = kwNorm(text)
  if (index) return index.get(n) || []
  return d.videos.filter(v => videoKwKeys(v).has(n))
}
/** The videos of every keyword at once (normalised keyword → videos, in the videos' order): the Keywords page asks for
 *  them several times per row */
export function kwIndex(d: ProfileData): Map<string, Video[]> {
  const m = new Map<string, Video[]>()
  d.videos.forEach(v => videoKwKeys(v).forEach(k => { const l = m.get(k); if (l) l.push(v); else m.set(k, [v]) }))
  return m
}
/** "Used": marked by hand, or in the SEO card of a video */
export const kwUsed = (d: ProfileData, k: Keyword, index?: Map<string, Video[]>): boolean => k.status === 'usata' || kwVideos(d, k.text, index).length > 0
/** Keywords shown by a filter chip */
export const filterKeywords = (d: ProfileData, list: readonly Keyword[], filter: KwFilter, index?: Map<string, Video[]>): Keyword[] =>
  list.filter(k => filter === 'all' || (filter === 'todo' ? !kwUsed(d, k, index) : kwUsed(d, k, index)))

/** Marks a keyword as used / to use; false if it does not exist */
export function toggleKeyword(d: ProfileData, id: string): boolean {
  const k = d.keywords.find(x => x.id === id)
  if (!k) return false
  k.status = k.status === 'usata' ? 'da usare' : 'usata'
  return true
}
export function removeKeyword(d: ProfileData, id: string): boolean {
  return removeItem(d, 'keywords', id)
}

/** New idea in the plan (Idea column) with the keyword already in the SEO card; the keyword becomes "used".
 *  seoDefaults: the empty SEO card (SEO_DEF of the video window). Returns the new video's title, or null. */
export function keywordToIdea(d: ProfileData, id: string, seoDefaults: VideoSeo, now = Date.now()): string | null {
  const k = d.keywords.find(x => x.id === id)
  if (!k) return null
  const title = kwTitle(k.text)
  d.videos.push(newVideo(d, { id: 'v_' + now, title, projectId: k.projectId as string }, { seo: { ...seoDefaults, kw: k.text, variants: [] } }))
  k.status = 'usata'
  return title
}
/** New hook titled like the keyword (the keyword's status does not change). Returns its title, or null. */
export function keywordToHook(d: ProfileData, id: string, now = Date.now()): string | null {
  const k = d.keywords.find(x => x.id === id)
  if (!k) return null
  const title = kwTitle(k.text)
  const hook: Hook = { id: 'vt_' + now, title, tags: [], projectId: k.projectId, content: '' }
  d.vault.push(hook)
  return title
}
